/**
 * محرك التحليل السلوكي - Behavioral Analytics Engine
 * يتتبع ويحلل سلوك المستخدمين لاكتشاف الأنماط والتنبؤ بالاحتياجات
 */

export class BehavioralAnalyticsEngine {
  constructor() {
    this.userProfiles = new Map();
    this.interactionHistory = [];
    this.patterns = new Map();
  }

  /**
   * تتبع تفاعل المستخدم
   */
  trackInteraction(userId, interaction) {
    const timestamp = new Date();
    
    const trackedInteraction = {
      userId,
      timestamp,
      type: interaction.type, // 'page_view', 'click', 'chat_message', 'form_submit'
      page: interaction.page,
      duration: interaction.duration,
      action: interaction.action,
      metadata: interaction.metadata
    };

    this.interactionHistory.push(trackedInteraction);

    // تحديث ملف المستخدم
    this.updateUserProfile(userId, trackedInteraction);

    // تحليل الأنماط
    this.analyzePatterns(userId);

    return trackedInteraction;
  }

  /**
   * تحديث الملف السلوكي للمستخدم
   */
  updateUserProfile(userId, interaction) {
    if (!this.userProfiles.has(userId)) {
      this.userProfiles.set(userId, {
        userId,
        firstSeen: interaction.timestamp,
        lastSeen: interaction.timestamp,
        totalInteractions: 0,
        pageViews: {},
        clickPatterns: [],
        chatHistory: [],
        avgSessionDuration: 0,
        preferredPages: [],
        issueHistory: [],
        satisfactionScore: 0,
        riskLevel: 'low', // low, medium, high, critical
        predictedNeeds: [],
        behavioralFlags: []
      });
    }

    const profile = this.userProfiles.get(userId);
    
    profile.lastSeen = interaction.timestamp;
    profile.totalInteractions++;

    // تتبع الصفحات
    if (interaction.type === 'page_view') {
      profile.pageViews[interaction.page] = (profile.pageViews[interaction.page] || 0) + 1;
    }

    // تتبع النقرات
    if (interaction.type === 'click') {
      profile.clickPatterns.push({
        action: interaction.action,
        timestamp: interaction.timestamp
      });
    }

    // تتبع المحادثات
    if (interaction.type === 'chat_message') {
      profile.chatHistory.push({
        message: interaction.metadata?.message,
        sentiment: this.analyzeSentiment(interaction.metadata?.message),
        timestamp: interaction.timestamp
      });
    }

    // حساب متوسط مدة الجلسة
    if (interaction.duration) {
      const totalDuration = profile.avgSessionDuration * (profile.totalInteractions - 1) + interaction.duration;
      profile.avgSessionDuration = totalDuration / profile.totalInteractions;
    }

    this.userProfiles.set(userId, profile);
  }

  /**
   * تحليل المشاعر من النص (بسيط)
   */
  analyzeSentiment(text) {
    if (!text) return 'neutral';

    const negativeWords = ['مشكلة', 'خطأ', 'فشل', 'سيء', 'غاضب', 'محبط', 'متأخر', 'ضربني', 'خايفة'];
    const positiveWords = ['شكراً', 'ممتاز', 'رائع', 'جيد', 'سعيد', 'راض'];
    const urgentWords = ['ضروري', 'عاجل', 'طارئ', 'فوري', 'سريع'];

    const lowerText = text.toLowerCase();
    
    let score = 0;
    negativeWords.forEach(word => {
      if (lowerText.includes(word)) score -= 1;
    });
    positiveWords.forEach(word => {
      if (lowerText.includes(word)) score += 1;
    });

    const isUrgent = urgentWords.some(word => lowerText.includes(word));

    if (score < -1) return isUrgent ? 'very_negative_urgent' : 'very_negative';
    if (score < 0) return isUrgent ? 'negative_urgent' : 'negative';
    if (score > 1) return 'very_positive';
    if (score > 0) return 'positive';
    return isUrgent ? 'neutral_urgent' : 'neutral';
  }

  /**
   * تحليل الأنماط السلوكية
   */
  analyzePatterns(userId) {
    const profile = this.userProfiles.get(userId);
    if (!profile) return;

    const patterns = [];

    // نمط 1: زيارات متكررة لنفس الصفحة (مشكلة محتملة)
    const mostVisitedPage = Object.entries(profile.pageViews)
      .sort(([, a], [, b]) => b - a)[0];
    
    if (mostVisitedPage && mostVisitedPage[1] > 3) {
      patterns.push({
        type: 'repeated_page_visits',
        page: mostVisitedPage[0],
        count: mostVisitedPage[1],
        severity: mostVisitedPage[1] > 5 ? 'high' : 'medium',
        prediction: 'المستخدم قد يواجه صعوبة في إيجاد ما يبحث عنه',
        suggestedAction: 'تقديم مساعدة مباشرة أو دليل إرشادي'
      });
    }

    // نمط 2: رسائل سلبية متكررة
    const recentNegativeMessages = profile.chatHistory
      .filter(chat => chat.sentiment.includes('negative'))
      .slice(-3);

    if (recentNegativeMessages.length >= 2) {
      patterns.push({
        type: 'negative_sentiment_trend',
        count: recentNegativeMessages.length,
        severity: 'high',
        prediction: 'المستخدم يواجه مشكلة جدية ويحتاج تدخل فوري',
        suggestedAction: 'تصعيد للمختص أو تقديم دعم مباشر'
      });
      
      profile.riskLevel = 'high';
    }

    // نمط 3: جلسات طويلة بدون إنجاز
    if (profile.avgSessionDuration > 300 && profile.totalInteractions > 10) {
      patterns.push({
        type: 'long_unproductive_sessions',
        avgDuration: profile.avgSessionDuration,
        severity: 'medium',
        prediction: 'المستخدم يقضي وقت طويل بدون إنجاز المهمة',
        suggestedAction: 'تبسيط العملية أو تقديم مساعدة تفاعلية'
      });
    }

    // نمط 4: كلمات عاجلة/طارئة
    const urgentMessages = profile.chatHistory
      .filter(chat => chat.sentiment.includes('urgent'));

    if (urgentMessages.length > 0) {
      patterns.push({
        type: 'urgent_request',
        count: urgentMessages.length,
        severity: 'critical',
        prediction: 'حالة طارئة تحتاج استجابة فورية',
        suggestedAction: 'تحويل فوري للمختص + إرسال موارد طوارئ'
      });
      
      profile.riskLevel = 'critical';
    }

    // حفظ الأنماط
    this.patterns.set(userId, patterns);
    profile.behavioralFlags = patterns;

    return patterns;
  }

  /**
   * التنبؤ بالاحتياجات المستقبلية
   */
  predictNeeds(userId) {
    const profile = this.userProfiles.get(userId);
    if (!profile) return [];

    const predictions = [];

    // تنبؤ 1: بناءً على تاريخ المشاكل
    if (profile.issueHistory.length > 0) {
      const lastIssue = profile.issueHistory[profile.issueHistory.length - 1];
      const daysSinceLastIssue = (new Date() - new Date(lastIssue.date)) / (1000 * 60 * 60 * 24);

      if (daysSinceLastIssue > 25 && daysSinceLastIssue < 35) {
        predictions.push({
          type: 'recurring_issue',
          probability: 0.75,
          description: 'احتمال تكرار نفس المشكلة خلال أيام',
          suggestedAction: 'إرسال تذكير استباقي أو حل وقائي',
          category: lastIssue.category
        });
      }
    }

    // تنبؤ 2: بناءً على الأنماط السلوكية
    const patterns = this.patterns.get(userId) || [];
    patterns.forEach(pattern => {
      if (pattern.severity === 'high' || pattern.severity === 'critical') {
        predictions.push({
          type: 'behavioral_prediction',
          probability: 0.85,
          description: pattern.prediction,
          suggestedAction: pattern.suggestedAction,
          category: pattern.type
        });
      }
    });

    // تنبؤ 3: بناءً على الوقت والموسمية
    const currentMonth = new Date().getMonth();
    if (currentMonth === 0 || currentMonth === 11) { // يناير أو ديسمبر
      predictions.push({
        type: 'seasonal_prediction',
        probability: 0.65,
        description: 'فترة ذروة للطلبات - احتمال حاجة للدعم',
        suggestedAction: 'تقديم موارد إضافية أو أسئلة شائعة',
        category: 'seasonal'
      });
    }

    profile.predictedNeeds = predictions;
    return predictions;
  }

  /**
   * الحصول على توصيات استباقية
   */
  getProactiveRecommendations(userId) {
    const profile = this.userProfiles.get(userId);
    if (!profile) return [];

    const predictions = this.predictNeeds(userId);
    const patterns = this.patterns.get(userId) || [];

    const recommendations = [];

    // توصية بناءً على مستوى الخطر
    if (profile.riskLevel === 'critical') {
      recommendations.push({
        priority: 'urgent',
        title: '🚨 تدخل فوري مطلوب',
        description: 'المستخدم في حالة طارئة',
        actions: [
          'تحويل فوري لمختص',
          'إرسال أرقام طوارئ',
          'فتح قناة اتصال مباشرة'
        ]
      });
    } else if (profile.riskLevel === 'high') {
      recommendations.push({
        priority: 'high',
        title: '⚠️ مستخدم يحتاج دعم عاجل',
        description: 'اكتشاف مشكلة جدية',
        actions: [
          'تقديم مساعدة مباشرة',
          'تصعيد للفريق المختص',
          'متابعة دورية'
        ]
      });
    }

    // توصيات بناءً على التنبؤات
    predictions.forEach(prediction => {
      if (prediction.probability > 0.7) {
        recommendations.push({
          priority: 'medium',
          title: '💡 اقتراح استباقي',
          description: prediction.description,
          actions: [prediction.suggestedAction]
        });
      }
    });

    return recommendations;
  }

  /**
   * الحصول على ملخص التحليل
   */
  getAnalyticsSummary() {
    const totalUsers = this.userProfiles.size;
    const totalInteractions = this.interactionHistory.length;
    
    let criticalUsers = 0;
    let highRiskUsers = 0;
    let satisfiedUsers = 0;

    this.userProfiles.forEach(profile => {
      if (profile.riskLevel === 'critical') criticalUsers++;
      else if (profile.riskLevel === 'high') highRiskUsers++;
      
      if (profile.satisfactionScore > 0.7) satisfiedUsers++;
    });

    return {
      totalUsers,
      totalInteractions,
      criticalUsers,
      highRiskUsers,
      satisfiedUsers,
      satisfactionRate: totalUsers > 0 ? (satisfiedUsers / totalUsers * 100).toFixed(1) : 0,
      avgInteractionsPerUser: totalUsers > 0 ? (totalInteractions / totalUsers).toFixed(1) : 0
    };
  }

  /**
   * تصدير بيانات المستخدم
   */
  getUserProfile(userId) {
    return this.userProfiles.get(userId);
  }

  /**
   * تصدير جميع الملفات
   */
  getAllProfiles() {
    return Array.from(this.userProfiles.values());
  }
}

// إنشاء instance واحد للاستخدام في التطبيق
export const behavioralEngine = new BehavioralAnalyticsEngine();
